/**
 * FE mirror of backend quotation-revision-policy (Wave 3).
 * Keep messages aligned with backend/src/modules/crm/constants/quotation-revision-policy.ts
 */

export type QuotationRevisionGateRow = {
  status?: string | null;
  approval_status?: string | null;
  is_converted_to_sales_order?: boolean;
  sales_order_id?: string | null;
  is_deleted?: boolean;
  is_current_submitted?: boolean | null;
  is_latest_revision?: boolean | null;
  superseded_at?: string | null;
};

export type GateCheck = { ok: boolean; reason?: string };

function norm(v: unknown): string {
  return String(v ?? '').trim().toLowerCase();
}

export function canCreateQuotationRevision(row: QuotationRevisionGateRow): GateCheck {
  if (row.is_deleted) {
    return { ok: false, reason: 'Cannot revise a deleted quotation' };
  }
  if (row.is_converted_to_sales_order || row.sales_order_id) {
    return {
      ok: false,
      reason: 'Cannot revise a quotation that has been converted to a customer order',
    };
  }

  const status = norm(row.status);
  const approval = norm(row.approval_status);

  if (status === 'cancelled' || status === 'expired' || status === 'rejected') {
    return { ok: false, reason: `Cannot create a revision from status ${row.status}` };
  }

  if (status === 'draft' && (approval === 'pending' || approval === '' || approval === 'rejected')) {
    return {
      ok: false,
      reason:
        'Draft quotations should be edited directly. Create a revision after approval or customer submission.',
    };
  }

  if (status === 'revised') {
    return {
      ok: false,
      reason: 'This row is already marked Revised. Open the latest revision to create a new one.',
    };
  }

  if (approval === 'approved' || status === 'sent' || status === 'accepted') {
    return { ok: true };
  }

  return {
    ok: false,
    reason:
      'Create Revision is available after internal approval or after the quotation is Sent/Accepted.',
  };
}

/** Display label: DB revision_number 1 → R0 */
export function displayQuotationRevisionLabel(revisionNumber?: number | null): string {
  const n = Number(revisionNumber);
  const safe = Number.isFinite(n) ? Math.trunc(n) : 1;
  return `R${safe - 1}`;
}

/** Print header / continuation: commercial number + display revision. */
export function formatQuotationPrintIdentity(
  quotationNumber?: string | null,
  revisionNumber?: number | null
): string {
  const num = String(quotationNumber || '').trim() || 'QUO';
  return `${num} ${displayQuotationRevisionLabel(revisionNumber)}`;
}

export const QUOTATION_REVISION_TYPE_OPTIONS = [
  { value: 'COMMERCIAL_ONLY', label: 'Commercial only' },
  { value: 'ESTIMATE_CHANGE', label: 'Estimate change' },
  { value: 'SCOPE_CHANGE', label: 'Scope change' },
  { value: 'QUANTITY_CHANGE', label: 'Quantity change' },
  { value: 'CUSTOMER_REQUEST', label: 'Customer request' },
  { value: 'TERMS_CHANGE', label: 'Terms change' },
  { value: 'NEGOTIATION', label: 'Negotiation' },
  { value: 'VALIDITY_EXTENSION', label: 'Validity extension' },
  { value: 'INTERNAL_CORRECTION', label: 'Internal correction' },
  { value: 'OTHER', label: 'Other' },
] as const;

/** FE mirror — Wave 6 send gate (keep aligned with backend policy). */
export function canSendQuotationToCustomer(row: QuotationRevisionGateRow): GateCheck {
  if (row.is_deleted) return { ok: false, reason: 'Cannot send a deleted quotation' };
  if (row.is_converted_to_sales_order || row.sales_order_id) {
    return { ok: false, reason: 'Quotation already converted to a customer order' };
  }
  const status = norm(row.status);
  const approval = norm(row.approval_status);
  if (status === 'sent') return { ok: false, reason: 'Quotation already sent' };
  if (status === 'revised') {
    return { ok: false, reason: 'Cannot send a superseded (Revised) quotation. Open the current revision.' };
  }
  if (status === 'accepted') return { ok: false, reason: 'Quotation already accepted' };
  if (approval !== 'approved') {
    return { ok: false, reason: 'Quotation must be approved before sending' };
  }
  return { ok: true };
}

/** FE mirror — Wave 7 SO convert gate. */
export function canConvertQuotationToSalesOrder(row: QuotationRevisionGateRow): GateCheck {
  if (row.is_deleted) return { ok: false, reason: 'Cannot convert a deleted quotation' };
  if (row.is_converted_to_sales_order || row.sales_order_id) {
    return { ok: false, reason: 'Quotation has already been converted to a customer order' };
  }
  const status = norm(row.status);
  if (status === 'revised' || row.superseded_at) {
    return {
      ok: false,
      reason: 'Cannot convert a superseded quotation revision. Convert the current accepted customer offer.',
    };
  }
  if (status !== 'accepted') {
    return { ok: false, reason: 'Only accepted quotations can be converted to customer orders' };
  }
  if (row.is_current_submitted === false) {
    return {
      ok: false,
      reason: 'Only the current customer-submitted revision can be converted.',
    };
  }
  return { ok: true };
}
